import { gql } from 'graphql-request'
import { useForm, Controller } from 'react-hook-form'
import { Check } from 'lucide-react'
import { useMutation } from '@hooks/graphql'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

const UPDATE_ORGANIZATION_PROFILE = gql`
  mutation updateOrganization($input: UpdateOrganizationInput!) {
    updateOrganization(input: $input) {
      organization {
        id
        name
        organizationType
        moderationPolicy
      }
      errors {
        message
      }
    }
  }
`

const Profile = ({ organizationId, name, organizationType, moderationPolicy, customModerationEnabled, refetch }) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({ mode: 'onTouched', defaultValues: { name, organizationType, moderationPolicy } })

  const { mutate: updateOrganization, isLoading: isUpdating, isSuccess } = useMutation({
    gqlMutation: UPDATE_ORGANIZATION_PROFILE,
    onSuccess: () => refetch()
  })

  const onSubmit = data => updateOrganization({ input: { id: organizationId, ...data } })

  return (
    <>
      <h1 className='text-2xl font-bold'>Profile</h1>
      <form
        className='flex flex-col mt-5 space-y-5'
        onSubmit={handleSubmit(onSubmit)}
      >
        <If condition={errors.general}>
          <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.general.message}</p>
        </If>

        <div className='space-y-1'>
          <Label htmlFor='name'>Name</Label>
          <Input
            className='w-full'
            id='name'
            {...register('name')}
          />
          <If condition={errors.name}>
            <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.name.message}</p>
          </If>
        </div>

        <div className='space-y-1'>
          <Label htmlFor='organization-type'>Organization type</Label>
          <Controller
            name='organizationType'
            control={control}
            render={({ field }) => (
              // eslint-disable-next-line react/jsx-handler-names
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <SelectTrigger id='organization-type' className='w-full'>
                  <SelectValue placeholder='Select organization type' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='SCHOOL_K12'>School (K12)</SelectItem>
                  <SelectItem value='UNIVERSITY'>University</SelectItem>
                  <SelectItem value='ONLINE_SCHOOL'>Online school</SelectItem>
                  <SelectItem value='MICROSCHOOL'>Microschool</SelectItem>
                  <SelectItem value='HOMESCHOOL'>Homeschool</SelectItem>
                  <SelectItem value='TUTOR_PROGRAMME'>Tutor programme</SelectItem>
                  <SelectItem value='NGO'>NGO</SelectItem>
                  <SelectItem value='OTHER'>Other</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <If condition={errors.organizationType}>
            <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.organizationType.message}</p>
          </If>
        </div>

        <If condition={customModerationEnabled}>
          <div className='space-y-2'>
            <Label>Moderation Policy</Label>
            <Controller
              name='moderationPolicy'
              control={control}
              render={({ field }) => (
                <RadioGroup
                  // eslint-disable-next-line react/jsx-handler-names
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <div className='space-y-1'>
                    <div className='flex items-center space-x-2'>
                      <RadioGroupItem value='STRICT' id='policy-strict' />
                      <Label htmlFor='policy-strict' className='font-normal'>Strict</Label>
                    </div>
                    <p className='text-sm text-muted-foreground ml-6'>
                      Designed for younger students. This blocks swearing and content related to violence, substance use, weapons, illegal activities, sexual content, harassment, and threats. It has particular sensitivity for topics around mental health and self-harm.
                    </p>
                  </div>

                  <div className='space-y-1'>
                    <div className='flex items-center space-x-2'>
                      <RadioGroupItem value='MODERATE' id='policy-moderate' />
                      <Label htmlFor='policy-moderate' className='font-normal'>Moderate</Label>
                    </div>
                    <p className='text-sm text-muted-foreground ml-6'>
                      Suitable for high school students. Blocks harmful content including credible threats, illegal activities, and targeted harassment, with particular sensitivity around content depicting self-harm or issues related to mental health. Allows academic discussion of mature topics, mild profanity, and sexual content within an educational context.
                    </p>
                  </div>

                  <div className='space-y-1'>
                    <div className='flex items-center space-x-2'>
                      <RadioGroupItem value='LENIENT' id='policy-lenient' />
                      <Label htmlFor='policy-lenient' className='font-normal'>Lenient</Label>
                    </div>
                    <p className='text-sm text-muted-foreground ml-6'>
                      For mature audiences. Allows mature academic discussion of sensitive topics. Only blocks content deemed as credible threats, illegal activities, targeted harassment, and non-educational sexual content. Sensitively blocks questions related to self-harm and mental health.
                    </p>
                  </div>
                </RadioGroup>
              )}
            />
            <If condition={errors.moderationPolicy}>
              <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.moderationPolicy.message}</p>
            </If>
          </div>
        </If>

        <div className='ml-auto flex items-center'>
          <If condition={isSuccess}>
            <Check size={20} className='mr-2' />
          </If>

          <Button
            className='w-fit'
            variant='default'
            disabled={isUpdating}
            loading={isUpdating}
            type='submit'
          >
            {isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </>
  )
}

export default Profile
